import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ExclamationTriangleIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  ScaleIcon,
  CalendarIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/utils/cn';
import {
  legalService,
  LegalMatter,
  MATTER_STATUSES,
  MATTER_TYPES,
  MATTER_PRIORITIES,
  MATTER_TYPE_LABELS,
  MATTER_STATUS_LABELS,
} from '../services/legal.service';

// ── Static configs ────────────────────────────────────────────────────────────

const CONTAINER_VARIANTS = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
};

const ITEM_VARIANTS = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.2, ease: 'easeOut' } },
};

const STATUS_BADGE_COLORS: Record<string, string> = {
  success: 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-300',
  warning: 'bg-warning-100 text-warning-800 dark:bg-warning-900/20 dark:text-warning-300',
  danger: 'bg-danger-100 text-danger-800 dark:bg-danger-900/20 dark:text-danger-300',
  secondary: 'bg-secondary-100 text-secondary-800 dark:bg-secondary-800 dark:text-secondary-300',
  primary: 'bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-300',
};

// ── MatterCard ────────────────────────────────────────────────────────────────

function MatterCard({ matter }: { matter: LegalMatter }) {
  const statusCfg = legalService.getStatusConfig(matter.status);
  const priorityCfg = legalService.getPriorityConfig(matter.priority);

  return (
    <Link to={`/legal/matters/${matter.id}`} className="block">
      <Card hover="default" padding="none" className="transition-all duration-200 cursor-pointer">
        <CardContent className="p-6">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-1">
                <span className="text-xs font-mono text-secondary-500 dark:text-secondary-400">
                  {matter.matterNumber}
                </span>
              </div>
              <h3 className="font-semibold text-lg text-secondary-900 dark:text-white truncate">
                {matter.title}
              </h3>
              {matter.summary && (
                <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1 line-clamp-2">
                  {matter.summary.replace(/<[^>]*>/g, '')}
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            <span
              className={cn(
                'px-3 py-1 text-xs font-medium rounded-full',
                STATUS_BADGE_COLORS[statusCfg.color]
              )}
            >
              {statusCfg.label}
            </span>
            <span
              className={cn(
                'px-3 py-1 text-xs font-medium rounded-full',
                STATUS_BADGE_COLORS[priorityCfg.color]
              )}
            >
              {priorityCfg.label} Priority
            </span>
            <span className="px-3 py-1 text-xs font-medium bg-secondary-100 text-secondary-700 dark:bg-secondary-800 dark:text-secondary-300 rounded-full">
              {MATTER_TYPE_LABELS[matter.type] ?? matter.type}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <div className="flex items-center gap-2">
              <UserIcon className="w-4 h-4 text-secondary-500 dark:text-secondary-400 flex-shrink-0" />
              <span className="text-secondary-600 dark:text-secondary-400 truncate">
                {matter.leadAttorneyName}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <CalendarIcon className="w-4 h-4 text-secondary-500 dark:text-secondary-400 flex-shrink-0" />
              <span className="text-secondary-600 dark:text-secondary-400">
                Opened {legalService.formatDate(matter.dateOpened)}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function MattersList() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [matters, setMatters] = useState<LegalMatter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalMatters, setTotalMatters] = useState(0);

  const fetchMatters = useCallback(async () => {
    try {
      setLoading(true);
      const data = await legalService.getMatters({
        page: currentPage,
        limit: 20,
        status: selectedStatus !== 'all' ? selectedStatus : undefined,
        type: selectedType !== 'all' ? selectedType : undefined,
        priority: selectedPriority !== 'all' ? selectedPriority : undefined,
        search: searchQuery || undefined,
        sortBy: 'createdAt',
        sortOrder: 'desc',
      });
      setMatters(data.matters);
      setTotalPages(data.pagination.totalPages);
      setTotalMatters(data.pagination.total);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch matters:', err instanceof Error ? err.message : err);
      setError('Failed to load legal matters');
      setMatters([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, selectedStatus, selectedType, selectedPriority, searchQuery]);

  useEffect(() => {
    fetchMatters();
  }, [fetchMatters]);

  // Derived stats from current page data
  const activeCount = matters.filter((m) => m.status === 'ACTIVE').length;
  const criticalCount = matters.filter((m) => m.priority === 'CRITICAL').length;
  const onHoldCount = matters.filter((m) => m.status === 'ON_HOLD').length;

  const selectCls =
    'px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm';

  return (
    <motion.div
      variants={CONTAINER_VARIANTS}
      initial="hidden"
      animate="visible"
      className="p-4 sm:p-6 space-y-6"
    >
      {/* Header */}
      <motion.div variants={ITEM_VARIANTS}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">
              Legal Matters
            </h1>
            <p className="text-secondary-600 dark:text-secondary-400 mt-1">
              Manage legal cases, matters, and related documentation
            </p>
          </div>
          <Link to="/legal/matters/create">
            <Button className="w-full sm:w-auto">
              <PlusIcon className="w-4 h-4 mr-2" />
              New Matter
            </Button>
          </Link>
        </div>
      </motion.div>

      {/* Stats */}
      <motion.div variants={ITEM_VARIANTS}>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-secondary-900 dark:text-white">
                {totalMatters}
              </div>
              <div className="text-sm text-secondary-600 dark:text-secondary-400">
                Total Matters
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                {activeCount}
              </div>
              <div className="text-sm text-secondary-600 dark:text-secondary-400">Active</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-danger-600 dark:text-danger-400">
                {criticalCount}
              </div>
              <div className="text-sm text-secondary-600 dark:text-secondary-400">
                Critical Priority
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-warning-600 dark:text-warning-400">
                {onHoldCount}
              </div>
              <div className="text-sm text-secondary-600 dark:text-secondary-400">On Hold</div>
            </CardContent>
          </Card>
        </div>
      </motion.div>

      {/* Filters */}
      <motion.div variants={ITEM_VARIANTS}>
        <Card>
          <CardContent className="p-6">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1">
                <Input
                  type="text"
                  placeholder="Search matters..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  leftIcon={<MagnifyingGlassIcon className="w-4 h-4" />}
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <select
                  value={selectedStatus}
                  onChange={(e) => { setSelectedStatus(e.target.value); setCurrentPage(1); }}
                  className={selectCls}
                >
                  <option value="all">All Statuses</option>
                  {MATTER_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {MATTER_STATUS_LABELS[s] ?? s}
                    </option>
                  ))}
                </select>
                <select
                  value={selectedType}
                  onChange={(e) => { setSelectedType(e.target.value); setCurrentPage(1); }}
                  className={selectCls}
                >
                  <option value="all">All Types</option>
                  {MATTER_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {MATTER_TYPE_LABELS[t] ?? t}
                    </option>
                  ))}
                </select>
                <select
                  value={selectedPriority}
                  onChange={(e) => { setSelectedPriority(e.target.value); setCurrentPage(1); }}
                  className={selectCls}
                >
                  <option value="all">All Priorities</option>
                  {MATTER_PRIORITIES.map((p) => (
                    <option key={p} value={p}>
                      {p.charAt(0) + p.slice(1).toLowerCase()}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* List */}
      <motion.div variants={ITEM_VARIANTS}>
        {loading ? (
          <Card>
            <CardContent className="p-12 text-center">
              <div className="text-lg text-secondary-600 dark:text-secondary-400">
                Loading matters...
              </div>
            </CardContent>
          </Card>
        ) : error ? (
          <Card>
            <CardContent className="p-12 text-center">
              <ExclamationTriangleIcon className="w-12 h-12 text-danger-400 dark:text-danger-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
                Error Loading Matters
              </h3>
              <p className="text-secondary-600 dark:text-secondary-400 mb-4">{error}</p>
              <Button onClick={fetchMatters} variant="outline">
                Try Again
              </Button>
            </CardContent>
          </Card>
        ) : matters.length > 0 ? (
          <div className="space-y-4">
            {matters.map((matter) => (
              <MatterCard key={matter.id} matter={matter} />
            ))}

            {/* Pagination */}
            <div className="flex items-center justify-between pt-2">
              <span className="text-sm text-secondary-600 dark:text-secondary-400">
                Showing {matters.length} of {totalMatters} matters
              </span>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage <= 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-secondary-600 dark:text-secondary-400">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage >= totalPages}
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <ScaleIcon className="w-12 h-12 text-secondary-400 dark:text-secondary-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
                No matters found
              </h3>
              <p className="text-secondary-600 dark:text-secondary-400">
                No legal matters match your current filters. Try adjusting your search criteria or
                create a new matter.
              </p>
            </CardContent>
          </Card>
        )}
      </motion.div>
    </motion.div>
  );
}
